import { useState, useEffect } from 'react';
import adminService from '../../services/admin';
import toast from 'react-hot-toast';

function PaymentHistory() {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadData();
  }, [days, page]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [paymentsData, summaryData] = await Promise.all([
        adminService.getPayments(days, page, 20),
        adminService.getPaymentSummary(days),
      ]);
      setPayments(paymentsData.payments);
      setTotalPages(Math.ceil(paymentsData.total / 20));
      setSummary(summaryData);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'bg-green-500/20 text-green-400';
      case 'pending':
      case 'processing':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'failed':
      case 'cancelled':
        return 'bg-red-500/20 text-red-400';
      case 'refunded':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (isLoading && payments.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="spinner w-12 h-12"></span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Payment History</h1>
        <select
          value={days}
          onChange={(e) => {
            setDays(parseInt(e.target.value));
            setPage(1);
          }}
          className="input"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Total Revenue</p>
            <p className="text-2xl font-bold text-green-400">
              ${summary.total_revenue?.toFixed(2) || '0.00'}
            </p>
          </div>
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Transactions</p>
            <p className="text-2xl font-bold">{summary.total_transactions || 0}</p>
          </div>
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Successful</p>
            <p className="text-2xl font-bold text-green-400">
              {summary.successful_transactions || 0}
            </p>
          </div>
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Refunded</p>
            <p className="text-2xl font-bold text-blue-400">
              ${summary.refunded_amount?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>
      )}

      {/* Revenue by Provider */}
      {summary?.by_provider && Object.keys(summary.by_provider).length > 0 && (
        <div className="card mb-6">
          <h3 className="text-lg font-semibold mb-4">Revenue by Provider</h3>
          <div className="flex gap-6">
            {Object.entries(summary.by_provider).map(([provider, amount]) => (
              <div key={provider} className="flex items-center gap-2">
                <span className="capitalize">{provider}:</span>
                <span className="text-green-400 font-semibold">
                  ${amount.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payments Table */}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="pb-3 px-4">Date</th>
              <th className="pb-3 px-4">User</th>
              <th className="pb-3 px-4">Plan</th>
              <th className="pb-3 px-4">Provider</th>
              <th className="pb-3 px-4">Amount</th>
              <th className="pb-3 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => (
              <tr key={payment.id} className="border-b border-gray-800">
                <td className="py-4 px-4 text-sm">
                  {new Date(payment.created_at).toLocaleString()}
                </td>
                <td className="py-4 px-4 text-sm text-gray-400">
                  {payment.user_id?.slice(0, 8)}...
                </td>
                <td className="py-4 px-4">
                  <span className="text-sm">
                    {payment.plan_name || '-'}
                    {payment.billing_period && (
                      <span className="text-gray-500 ml-1">
                        ({payment.billing_period})
                      </span>
                    )}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <span className="capitalize text-sm">{payment.provider}</span>
                </td>
                <td className="py-4 px-4">
                  <span className="font-semibold">
                    ${payment.amount.toFixed(2)}
                  </span>
                  <span className="text-gray-500 text-sm ml-1">
                    {payment.currency}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <span
                    className={`px-2 py-1 rounded text-xs ${getStatusColor(
                      payment.status
                    )}`}
                  >
                    {payment.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {payments.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            No payments found for the selected period.
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-gray-700">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="btn btn-secondary btn-sm"
            >
              Previous
            </button>
            <span className="text-gray-400">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="btn btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentHistory;
